import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Table, Input, Button, Space, Collapse } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import CardPage from "../../../layout/CardPage";
import { useGetAsignaciones } from "./useGetAsignaciones";
import { setFiltrosVisorAsignacion } from "../../../context/actions/semaforoActions";

export const SemaforoAsignacion = () => {
  const { asignaciones, loading, error } = useGetAsignaciones();
  const dispatch = useDispatch();
  const { filtrosVisorAsignacion } = useSelector((state) => state.semaforoReducer);
  const { searchId, searchOrigen, searchDestino, filteredServicios, searchServicio } = filtrosVisorAsignacion;

  const updateFiltro = (filtro) => {
    dispatch(setFiltrosVisorAsignacion(filtro));
  };

  const serviciosUnicos = [...new Set(asignaciones.map((item) => item.servicio).filter(Boolean))];

  const gruposPorUsuario = useMemo(() => {
    const grupos = {};
    asignaciones.forEach((item) => {
      const usuario = item.usuarioIniciaAsignacion || "Sin asignación";
      if (!grupos[usuario]) grupos[usuario] = [];
      grupos[usuario].push(item);
    });
    return grupos;
  }, [asignaciones]);

  const columns = [
    {
      title: "Asistencia ID",
      dataIndex: "asistenciaID",
      key: "asistenciaID",
      width: 150,
      render: (asistenciaID) => (
        <Link to={`/detalleAsistencia/${asistenciaID}`}>{asistenciaID}</Link>
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Buscar ID"
            value={selectedKeys[0]}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
              updateFiltro({ searchId: value });
              confirm({ closeDropdown: false });
            }}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
            >
              Buscar
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                updateFiltro({ searchId: "" });
                confirm();
              }}
              size="small"
            >
              Limpiar
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) =>
        record.asistenciaID.toLowerCase().includes(value.toLowerCase()),
      filteredValue: searchId ? [searchId] : null,
    },
    {
      title: "Fecha Captura",
      dataIndex: "fechaCapturaTerminada",
      key: "fechaCapturaTerminada",
      width: 180,
      render: (fecha) => fecha ? new Date(fecha).toLocaleString() : "",
      sorter: (a, b) => new Date(a.fechaCapturaTerminada) - new Date(b.fechaCapturaTerminada),
    },
    {
      title: "Fecha Asignación",
      dataIndex: "fechaIniciaAsignacion",
      key: "fechaIniciaAsignacion",
      width: 180,
      render: (fecha) => fecha ? new Date(fecha).toLocaleString() : "",
      sorter: (a, b) => new Date(a.fechaIniciaAsignacion) - new Date(b.fechaIniciaAsignacion),
    },
    {
      title: "Servicio",
      dataIndex: "servicio",
      key: "servicio",
      width: 160,
      filters: serviciosUnicos.map((servicio) => ({ text: servicio, value: servicio })),
      onFilter: (value, record) => record.servicio === value,
      filteredValue: filteredServicios,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Buscar servicio"
            value={searchServicio}
            onChange={(e) => {
              updateFiltro({ searchServicio: e.target.value });
            }}
            style={{ marginBottom: 8, display: "block" }}
          />
          <div style={{ maxHeight: 200, overflowY: "auto", marginBottom: 8 }}>
            {serviciosUnicos
              .filter((servicio) =>
                servicio.toLowerCase().includes(searchServicio.toLowerCase())
              )
              .map((servicio) => (
                <div key={servicio} style={{ padding: "4px 0" }}>
                  <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={selectedKeys.includes(servicio)}
                      onChange={(e) => {
                        const newKeys = e.target.checked
                          ? [...selectedKeys, servicio]
                          : selectedKeys.filter((k) => k !== servicio);
                        setSelectedKeys(newKeys);
                        updateFiltro({ filteredServicios: newKeys });
                        confirm({ closeDropdown: false });
                      }}
                      style={{ marginRight: 8 }}
                    />
                    {servicio}
                  </label>
                </div>
              ))}
          </div>
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
            >
              OK
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                updateFiltro({ filteredServicios: [], searchServicio: "" });
                confirm();
              }}
              size="small"
            >
              Limpiar
            </Button>
          </Space>
        </div>
      ),
    },

    {
      title: "Dirección Origen",
      dataIndex: "direccionOrigen",
      key: "direccionOrigen",
      width: 200,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Buscar dirección"
            value={selectedKeys[0]}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
              updateFiltro({ searchOrigen: value });
              confirm({ closeDropdown: false });
            }}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
            >
              Buscar
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                updateFiltro({ searchOrigen: "" });
                confirm();
              }}
              size="small"
            >
              Limpiar
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) =>
        record.direccionOrigen?.toLowerCase().includes(value.toLowerCase()),
      filteredValue: searchOrigen ? [searchOrigen] : null,
    },
    {
      title: "Dirección Destino",
      dataIndex: "direccionDestino",
      key: "direccionDestino",
      width: 200,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Buscar dirección"
            value={selectedKeys[0]}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
              updateFiltro({ searchDestino: value });
              confirm({ closeDropdown: false });
            }}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
            >
              Buscar
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                updateFiltro({ searchDestino: "" });
                confirm();
              }}
              size="small"
            >
              Limpiar
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) =>
        record.direccionDestino?.toLowerCase().includes(value.toLowerCase()),
      filteredValue: searchDestino ? [searchDestino] : null,
    },
  ];

  if (error) return <Alert type="error" message="No se pudo consultar el semáforo de asignación" />;

  return (
    <CardPage titulo="Semáforo Asignación">
      <Collapse defaultActiveKey={Object.keys(gruposPorUsuario)}>
        {Object.entries(gruposPorUsuario).map(([usuario, data]) => (
          <Collapse.Panel key={usuario} header={`${usuario} (${data.length})`}>
            <Table
              columns={columns}
              dataSource={data}
              rowKey="asistenciaID"
              loading={loading}
              pagination={{ position: ["bottomCenter"], pageSize: 5 }}
              size="small"
            />
          </Collapse.Panel>
        ))}
      </Collapse>
    </CardPage>
  );
};
